import React, { type ReactNode, useRef, useState } from 'react';

import Button from 'components/Button';
import { useId, useValueChangeEffect } from 'hooks';
import { goToElement, selectTabbable } from 'utils/dom';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

interface Props {
	/** Class names for the container. */
	className?: string;

	/** Number of items to render in the collapsed state. */
	collapsedItemsCount?: number;

	/**
	 * What to focus after expanding all items, the first new item's container or
	 * the first interactive/tabbable element in that container, if one exists.
	 */
	expandedItemFocusTarget?: 'container' | 'first-interactive';

	/** If all items should be visible initially. */
	isInitiallyExpanded?: boolean;

	/** Items to render. */
	items: ReactNode[];
}

/** Toggle if all or only the first X number of items should render. */
export default function ExpandableItems({
	className,
	collapsedItemsCount = 4,
	expandedItemFocusTarget = 'container',
	isInitiallyExpanded = false,
	items,
}: Props) {
	const { t } = useI18n();
	const itemsId = useId('expandable-items');
	const itemsContainerRef = useRef<HTMLDivElement>(null);
	const [isExpanded, setIsExpanded] = useState<boolean>(isInitiallyExpanded);
	const truthyItems = items.filter(is.truthyOrZero);
	const visibleItems = isExpanded
		? truthyItems
		: truthyItems.slice(0, collapsedItemsCount);

	useValueChangeEffect(isExpanded, () => {
		const firstExpandedItem =
			itemsContainerRef.current?.children[collapsedItemsCount];
		if (isExpanded && is.instance(firstExpandedItem, HTMLElement)) {
			const tabbables = selectTabbable(firstExpandedItem);
			if (expandedItemFocusTarget === 'first-interactive' && tabbables[0]) {
				tabbables[0].focus();
			} else {
				goToElement(firstExpandedItem);
			}
		}
	});

	if (visibleItems.length === 0) {
		return null;
	}

	return (
		<div className={className}>
			<div id={itemsId} ref={itemsContainerRef}>
				{visibleItems}
			</div>
			{items.length > collapsedItemsCount && (
				<Button
					variant="text"
					onClick={() => {
						setIsExpanded((current) => !current);
					}}
					aria-controls={itemsId}
					aria-expanded={isExpanded}
				>
					{isExpanded
						? t('product_list_filter_checkbox_hide_button')
						: t('product_list_filter_checkbox_show_button')}
				</Button>
			)}
		</div>
	);
}
ExpandableItems.displayName = 'ExpandableItems';
